import React from "react"
import type { TokenValues } from "@silversea-ssc/dotcom-read-model-types/Tokens"
import { match } from "ts-pattern"
import { Payment, RealSuiteNumber } from "@ssc-booking/api-contracts"
import { FareCode } from "@silversea-ssc/web-fares"
import useModal from "../ModalProvider/useModal"
import FarePenaltiesModal from "../../app-core/features/cancellation-policy/components/FarePenaltiesModal/FarePenaltiesModal"
import PaymentModal from "../PaymentModal/PaymentModal"
import WebQuoteModal from "../../app-core/features/web-quote/WebQuoteModal/WebQuoteModal"
import SuitesComparisonModal from "../SuitesComparisonModal/SuitesComparisonModal"
import { SuiteWithPriceAndConfiguration } from "../../app-core/features/_common/read-models/suite-category"
import { SanitySuite, ShipDeckPlan } from "../../app-core/_common/sanity/sanity-types"
import DeckPlanModal from "../DeckPlanModal/DeckPlanModal"
import TestModal from "../TestModal/TestModal"
import InclusionsModal, { TabContent } from "../InclusionsModal/InclusionsModal"
import ReceiptModal from "../BottomBar/ReceiptModal"
import WebQuoteExitIntentModal from "../WebQuoteExitIntentModal/WebQuoteExitIntentModal"
import { FetchPageContent } from "../../app-core/_common/sanity/fetch-page-content"
import { WebQuoteExitIntent } from "../../app-core/features/_common/web-quote-exit-intent"
import AppSuiteModal from "./AppSuiteModal"
import AppPublicAreaModal from "./AppPublicAreaModal"

export type ModalProps =
    | SuiteModalProps
    | FareInclusionModalProps
    | PenaltiesModalProps
    | PaymentModalProps
    | WebQuoteGuestInfoModalProps
    | SuitesComparisonModalProps
    | DeckPlanModalProps
    | PublicAreaModalProps
    | TestModalProps
    | ReceiptModalProps
    | QuoteExitIntentModalProps

type ModalCommonProps = {
    skipBrowserHistory?: boolean
    onClose?: () => void
}

type DeckPlanModalProps = ModalCommonProps & {
    modalType: "deck-plan"
    shipDeckPlan: ShipDeckPlan
    availableSuiteNumbers: readonly RealSuiteNumber[]
    suitesContent: readonly SanitySuite[]
}

type PenaltiesModalProps = ModalCommonProps & {
    modalType: "penalties"
    selectedFareCode: FareCode
    showOnlySelectedFareCode?: boolean
}
export type SuiteModalProps = ModalCommonProps & {
    modalType: "suite"
    suiteCategory: SuiteWithPriceAndConfiguration<SanitySuite>
    shipVersionKey: string
}
type FareInclusionModalProps = ModalCommonProps & {
    modalType: "fare-inclusion"
    selectedStepGroupId: string
    tabs: TabContent[]
    fareTitle: string
    inclusionsContentTokens: TokenValues
}
type PaymentModalProps = ModalCommonProps & {
    modalType: "payment"
    payment: Payment
    iframeName: string
    onRequestClose: () => void
}
type WebQuoteGuestInfoModalProps = ModalCommonProps & {
    modalType: "web-quote-guest-info"
}
type QuoteExitIntentModalProps = ModalCommonProps & {
    modalType: "web-quote-exit-intent"
}
type SuitesComparisonModalProps = ModalCommonProps & {
    modalType: "suites-comparison"
    suiteCategory?: string
}

type ReceiptModalProps = ModalCommonProps & {
    modalType: "receipt"
    sanityContent: FetchPageContent
    webQuoteExitIntent: WebQuoteExitIntent
}

type PublicAreaModalProps = ModalCommonProps & {
    modalType: "public-area"
    areaType: "diningAreas" | "facilities"
    areaKey: string
}

type TestModalProps = ModalCommonProps & {
    modalType: "test"
}

type Props = { modalProps: ModalProps }
const AppModal: React.FC<React.PropsWithChildren<Props>> = ({ modalProps }) => {
    const { closeModal } = useModal()

    if (!modalProps) return <></>

    return match(modalProps)
        .with({ modalType: "web-quote-guest-info" }, () => <WebQuoteModal />)
        .with({ modalType: "suite" }, (props) => <AppSuiteModal modalState={props} />)
        .with({ modalType: "fare-inclusion" }, (props) => (
            <InclusionsModal
                title={props.fareTitle}
                dataContentName="inclusions_modal"
                modal={{
                    isOpen: true,
                    onRequestClose: () => closeModal("closeWithCallback"),
                }}
                size="big"
                inclusionsContentTokens={props.inclusionsContentTokens}
                selectedStepGroupId={props.selectedStepGroupId}
                tabs={props.tabs}
            />

            // <FareInfographicInclusionsModal
            //     title={props.fareTitle}
            //     dataContentName="inclusions_modal"
            //     modal={{
            //         isOpen: true,
            //         onRequestClose: () => closeModal("closeWithCallback"),
            //     }}
            //     size="big"
            //     inclusionsContent={props.inclusions}
            //     selectedInclusionId={props.inclusionId}
            //     inclusionsContentTokens={props.inclusionsContentTokens}
            // />
        ))
        .with({ modalType: "penalties" }, (props) => (
            <FarePenaltiesModal
                selectedFareCode={props.selectedFareCode}
                showOnlySelectedFareCode={props.showOnlySelectedFareCode}
            />
        ))
        .with({ modalType: "payment" }, (props) => (
            <PaymentModal iframeName={props.iframeName} closeModal={props.onRequestClose} payment={props.payment} />
        ))
        .with({ modalType: "suites-comparison" }, (props) => (
            <SuitesComparisonModal targetSuiteCategory={props.suiteCategory} />
        ))
        .with({ modalType: "receipt" }, (props) => <ReceiptModal {...props} />)
        .with({ modalType: "deck-plan" }, (props) => (
            <DeckPlanModal
                shipDeckPlan={props.shipDeckPlan}
                availableSuiteNumbers={props.availableSuiteNumbers}
                suitesContent={props.suitesContent}
            />
        ))
        .with({ modalType: "public-area" }, (props) => <AppPublicAreaModal {...props} />)
        .with({ modalType: "test" }, () => <TestModal />)
        .with({ modalType: "web-quote-exit-intent" }, () => <WebQuoteExitIntentModal />)
        .exhaustive()
}

export default React.memo(AppModal)
